import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getConsent, setConsent } from '../../utils/cookieConsent';

/**
 * Modale di gestione preferenze cookie.
 *
 * Aperta da `CookieBanner` (tasto "Personalizza") al primo ingresso, oppure
 * da un entry-point (footer/profilo) quando l'utente vuole revocare/modificare.
 *
 * Cattura lo stato corrente da localStorage all'apertura; le modifiche sono
 * locali fino a "Salva preferenze". I bottoni "Accetta tutti" / "Rifiuta tutti"
 * impostano il flag analytics e salvano in un colpo solo.
 */
const CookieSettings = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(false);

    // Quando si apre, ricarica lo stato salvato (utile se l'utente apre dal footer dopo aver già scelto)
    useEffect(() => {
        if (!isOpen) return;
        const existing = getConsent();
        setAnalytics(existing ? Boolean(existing.analytics) : false);
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSave = (overrideAnalytics) => {
        const finalAnalytics = typeof overrideAnalytics === 'boolean' ? overrideAnalytics : analytics;
        setConsent({ analytics: finalAnalytics });
        onClose?.();
    };

    return (
        <div
            className="fixed inset-0 bg-black/70 z-[60] flex flex-col items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
        >
            <div className="bg-white p-6 max-w-lg w-full rounded-xl flex flex-col gap-4 shadow-lg max-h-[90vh] overflow-y-auto">
                <h2 id="cookie-settings-title" className="text-lg font-semibold text-[#225424]">
                    {t('CookieSettings.title')}
                </h2>
                <p className="text-neutral-700 text-sm">
                    {t('CookieSettings.description')}{' '}
                    <Link to="/privacy-policy" className="underline text-[#225424]" onClick={onClose}>
                        Privacy Policy
                    </Link>.
                </p>

                {/* Necessari — sempre attivi */}
                <div className="border border-neutral-200 rounded-lg p-3 flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-[#225424]">{t('CookieSettings.necessaryTitle')}</h3>
                        <span className="text-xs uppercase tracking-wide text-[#225424] bg-[#22542410] px-2 py-1 rounded">
                            {t('CookieSettings.alwaysActive')}
                        </span>
                    </div>
                    <p className="text-sm text-neutral-600">{t('CookieSettings.necessaryDesc')}</p>
                </div>

                {/* Analitici — toggle */}
                <div className="border border-neutral-200 rounded-lg p-3 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-[#225424]">{t('CookieSettings.analyticsTitle')}</h3>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={analytics}
                                onChange={(e) => setAnalytics(e.target.checked)}
                                aria-label={t('CookieSettings.analyticsTitle')}
                            />
                            <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:bg-[#225424] transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
                        </label>
                    </div>
                    <p className="text-sm text-neutral-600">{t('CookieSettings.analyticsDesc')}</p>
                </div>

                {/* Azioni */}
                <div className="flex flex-col gap-2 pt-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            className="secondary w-full sm:flex-1"
                            onClick={() => handleSave(false)}
                        >
                            {t('CookieSettings.rejectAll')}
                        </button>
                        <button
                            type="button"
                            className="primary w-full sm:flex-1"
                            onClick={() => handleSave(true)}
                        >
                            {t('CookieSettings.acceptAll')}
                        </button>
                    </div>
                    <button
                        type="button"
                        className="primary w-full"
                        onClick={() => handleSave()}
                    >
                        {t('CookieSettings.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieSettings;
